import type { AdminRole } from "@repo/api-dtos"
import { useNavigate } from "@tanstack/react-router"
import * as React from "react"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminRoles } from "@/hooks/use-admin-roles"

interface AdminRolesContextValue {
	roles: AdminRole[]
	hasRole: (role: AdminRole) => boolean
	hasAnyRole: (roles: AdminRole[]) => boolean
	isSuperadmin: boolean
}

const AdminRolesContext = React.createContext<AdminRolesContextValue | null>(
	null,
)

export function useAdminRolesContext() {
	const context = React.useContext(AdminRolesContext)
	if (!context) {
		throw new Error(
			"useAdminRolesContext must be used within AdminRolesProvider",
		)
	}
	return context
}

export function AdminRolesProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const { roles, isLoading, error, hasRole, hasAnyRole, isSuperadmin } =
		useAdminRoles()
	const navigate = useNavigate()

	React.useEffect(() => {
		if (!isLoading && !error && roles.length === 0) {
			navigate({ to: "/unauthorized" })
		}
	}, [isLoading, error, roles, navigate])

	if (isLoading) {
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="space-y-4 w-64">
					<Skeleton className="h-8 w-full" />
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-4 w-1/2" />
				</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
					<p className="text-sm text-destructive">
						Failed to load admin roles. Please refresh the page.
					</p>
				</div>
			</div>
		)
	}

	if (roles.length === 0) {
		return null
	}

	return (
		<AdminRolesContext.Provider
			value={{ roles, hasRole, hasAnyRole, isSuperadmin }}
		>
			{children}
		</AdminRolesContext.Provider>
	)
}
